import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ShoppingCart, ArrowLeft, Plus, Minus, Heart } from 'lucide-react';
import { addToCart } from '../utils/cartUtils';
import { toast } from 'sonner';
import { getProduct, getProducts } from '../services/api';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const productData = await getProduct(id);
        setProduct(productData);
        
        // Get related products from same category
        const allProducts = await getProducts({ category: productData.category });
        const related = allProducts
          .filter(p => p.id !== productData.id)
          .slice(0, 4);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Error loading product:', error);
        toast.error('Product not found');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProductData();
  }, [id, navigate]);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${quantity} x ${product.name} added to cart!`);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => prev > 1 ? prev - 1 : 1);

  if (loading) {
    return <div className="loading-container">Loading product...</div>;
  }

  if (!product) {
    return null;
  }

  return (
    <div className="product-detail-page">
      <div className="product-detail-container">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="back-button"
        >
          <ArrowLeft size={18} className="mr-2" /> Back
        </Button>

        <div className="product-detail-grid">
          {/* Product Image */}
          <div className="product-detail-image-section">
            <img
              src={product.image}
              alt={product.name}
              className="product-detail-image"
            />
          </div>

          {/* Product Info */}
          <div className="product-detail-info">
            <p className="product-detail-category">{product.category}</p>
            <h1 className="product-detail-title">{product.name}</h1>
            <p className="product-detail-price">${product.price.toFixed(2)}</p>
            
            <p className="product-detail-description">{product.description}</p>

            <div className="product-detail-ingredients">
              <h3 className="ingredients-title">Ingredients</h3>
              <p className="ingredients-text">{product.ingredients}</p>
            </div>

            <div className="product-detail-stock">
              {product.inStock ? (
                <span className="in-stock">In Stock</span>
              ) : (
                <span className="out-of-stock">Out of Stock</span>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="quantity-section">
              <label className="quantity-label">Quantity:</label>
              <div className="quantity-controls">
                <button
                  onClick={decrementQuantity}
                  className="quantity-btn"
                  aria-label="Decrease quantity"
                >
                  <Minus size={16} />
                </button>
                <span className="quantity-display">{quantity}</span>
                <button
                  onClick={incrementQuantity}
                  className="quantity-btn"
                  aria-label="Increase quantity"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="product-actions">
              <Button
                size="lg"
                onClick={handleAddToCart}
                disabled={!product.inStock}
                className="add-to-cart-button"
              >
                <ShoppingCart size={20} className="mr-2" />
                Add to Cart
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="wishlist-button"
              >
                <Heart size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="related-products-section">
            <h2 className="related-products-title">You May Also Like</h2>
            <div className="related-products-grid">
              {relatedProducts.map(relatedProduct => (
                <Card key={relatedProduct.id} className="product-card">
                  <Link to={`/product/${relatedProduct.id}`}>
                    <div className="product-image-wrapper">
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                        className="product-image"
                      />
                    </div>
                    <div className="product-info">
                      <p className="product-category">{relatedProduct.category}</p>
                      <h3 className="product-name">{relatedProduct.name}</h3>
                      <p className="product-price">${relatedProduct.price.toFixed(2)}</p>
                    </div>
                  </Link>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
